import { AlertTriangle, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ResourceUsage, LimitResourceType } from '@/types/billing';
import { formatLimit, getResourceDisplayName, isAtLimit, isNearLimit } from '@/types/billing';
import { Button } from '@/components/ui/button';

interface PlanLimitWarningProps {
  /** Resource type being warned about */
  resourceType: LimitResourceType;
  /** Current usage statistics */
  usage: ResourceUsage;
  /** Whether to show inline or as a banner */
  variant?: 'inline' | 'banner';
  /** Callback when upgrade is clicked */
  onUpgradeClick?: () => void;
  /** Additional class names */
  className?: string;
}

/**
 * Warning component that shows when approaching or at plan limits
 */
export function PlanLimitWarning({
  resourceType,
  usage,
  variant = 'inline',
  onUpgradeClick,
  className,
}: PlanLimitWarningProps) {
  const atLimit = isAtLimit(usage);
  const nearLimit = isNearLimit(usage);

  // Don't show anything if not near or at limit
  if (!atLimit && !nearLimit) {
    return null;
  }

  const displayName = getResourceDisplayName(resourceType);
  const limitDisplay = formatLimit(usage.limit);

  if (variant === 'banner') {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-4 rounded-lg border px-4 py-3',
          atLimit
            ? 'border-destructive/50 bg-destructive/5 text-destructive'
            : 'border-warning/50 bg-warning/5 text-warning',
          className
        )}
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">
              {atLimit ? `${displayName} limit reached` : `Approaching ${displayName.toLowerCase()} limit`}
            </p>
            <p className="text-sm opacity-80">
              {atLimit
                ? `You've reached your limit of ${limitDisplay} ${displayName.toLowerCase()}.`
                : `You're using ${usage.current} of ${limitDisplay} ${displayName.toLowerCase()} (${usage.percentage}%).`}
            </p>
          </div>
        </div>
        {onUpgradeClick && (
          <Button
            variant={atLimit ? 'default' : 'outline'}
            size="sm"
            onClick={onUpgradeClick}
            className="shrink-0"
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            Upgrade
          </Button>
        )}
      </div>
    );
  }

  // Inline variant - smaller, for use near buttons/forms
  return (
    <p
      className={cn(
        'flex items-center gap-1.5 text-sm',
        atLimit ? 'text-destructive' : 'text-warning',
        className
      )}
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      <span>
        {atLimit
          ? `Limit reached (${usage.current}/${limitDisplay})`
          : `${usage.current}/${limitDisplay} used (${usage.percentage}%)`}
      </span>
      {onUpgradeClick && (
        <button
          type="button"
          onClick={onUpgradeClick}
          className="underline underline-offset-2 hover:no-underline"
        >
          Upgrade
        </button>
      )}
    </p>
  );
}

interface UsageBadgeProps {
  /** Current usage statistics */
  usage: ResourceUsage;
  /** Additional class names */
  className?: string;
}

/**
 * Small badge showing usage status
 */
export function UsageBadge({ usage, className }: UsageBadgeProps) {
  const atLimit = isAtLimit(usage);
  const nearLimit = isNearLimit(usage);

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        atLimit
          ? 'bg-destructive/10 text-destructive'
          : nearLimit
          ? 'bg-warning/10 text-warning'
          : 'bg-muted text-muted-foreground',
        className
      )}
    >
      {usage.current}/{formatLimit(usage.limit)}
    </span>
  );
}
